import { useCallback, useEffect, useRef, useState } from "react";
import {
  createFarmPrinter,
  deleteFarmPrinter,
  farmPrinterStatus,
  listFarmPrinters,
  type FarmPrinter,
  type FarmStatus,
} from "../api";
import { useToast } from "../lib/toast";
import { Icon } from "./Icon";
import { Empty, Modal } from "./ui";

const KINDS = [
  { id: "moonraker", label: "Moonraker (Klipper)" },
  { id: "octoprint", label: "OctoPrint" },
  { id: "prusalink", label: "PrusaLink" },
];

const STATE_LABEL: Record<string, string> = {
  printing: "Impression", queued: "Prêt", paused: "Pause",
  complete: "Terminé", error: "Erreur", offline: "Hors ligne",
};

function StatusBadge({ st }: { st: FarmStatus | undefined }) {
  if (!st) return <span className="badge"><span className="dot" />…</span>;
  if (st.online === false) return <span className="badge bad"><span className="dot" />Hors ligne</span>;
  if (st.online == null) return <span className="badge"><span className="dot" />Inconnu</span>;
  const lab = STATE_LABEL[st.state ?? ""] ?? st.state ?? "En ligne";
  const kind = st.state === "printing" ? "warn" : st.state === "error" ? "bad" : "ok";
  return <span className={`badge ${kind}`}><span className="dot" />{lab}</span>;
}

// The print farm: register network printers (Moonraker/OctoPrint/PrusaLink) and poll
// their live status. Polling fails soft -- an unreachable printer shows "Hors ligne".
export function Fleet() {
  const [printers, setPrinters] = useState<FarmPrinter[]>([]);
  const [statuses, setStatuses] = useState<Record<string, FarmStatus>>({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "moonraker", base_url: "", api_key: "" });
  const toast = useToast((s) => s.show);
  const timer = useRef<number | null>(null);

  const poll = useCallback(async (list: FarmPrinter[]) => {
    const entries = await Promise.allSettled(list.map((p) => farmPrinterStatus(p.id)));
    const next: Record<string, FarmStatus> = {};
    entries.forEach((e, i) => { if (e.status === "fulfilled") next[list[i].id] = e.value; });
    setStatuses(next);
  }, []);

  const load = useCallback(async () => {
    const list = await listFarmPrinters().catch(() => [] as FarmPrinter[]);
    setPrinters(list);
    if (list.length) poll(list);
  }, [poll]);

  useEffect(() => { load(); }, [load]);

  // Poll live status every 6s while there are printers registered.
  useEffect(() => {
    if (!printers.length) return;
    timer.current = window.setInterval(() => poll(printers), 6000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [printers, poll]);

  async function register() {
    try {
      await createFarmPrinter(form);
      setOpen(false); setForm({ name: "", kind: "moonraker", base_url: "", api_key: "" });
      toast("Imprimante enregistrée"); load();
    } catch (e) { toast(String((e as Error).message), "bad"); }
  }
  async function remove(id: string) {
    try { await deleteFarmPrinter(id); toast("Imprimante retirée", "bad"); load(); }
    catch (e) { toast(String((e as Error).message), "bad"); }
  }

  return (
    <div style={{ marginBottom: 30 }}>
      <div className="row between" style={{ marginBottom: 14 }}>
        <div className="row" style={{ gap: 10 }}>
          <h2>Ferme d'impression</h2>
          <span className="badge"><span className="num">{printers.length}</span></span>
        </div>
        <button className="btn sm primary" onClick={() => setOpen(true)}><Icon name="plus" size={14} /> Ajouter une imprimante</button>
      </div>

      {printers.length === 0 ? (
        <Empty icon="wifi">Aucune imprimante réseau. Ajoute une imprimante Moonraker / OctoPrint / PrusaLink pour router et suivre les impressions en direct.</Empty>
      ) : (
        <div className="grid cards" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
          {printers.map((p) => {
            const st = statuses[p.id];
            return (
              <div key={p.id} className="card">
                <div className="card-row" style={{ marginBottom: 8 }}>
                  <div className="row" style={{ gap: 9 }}>
                    <span style={{ color: st?.online ? "var(--ok)" : "var(--muted)" }}><Icon name="wifi" size={16} /></span>
                    <span className="card-title">{p.name}</span>
                  </div>
                  <StatusBadge st={st} />
                </div>
                <div className="muted num" style={{ fontSize: 11.5, wordBreak: "break-all" }}>{p.kind} · {p.base_url}</div>
                {st?.online && st.state === "printing" && (
                  <>
                    <div className="progress" style={{ marginTop: 10, height: 5 }}><span style={{ width: `${Math.round((st.progress || 0) * 100)}%`, background: "var(--warn)" }} /></div>
                    <div className="row between" style={{ marginTop: 5 }}>
                      <span className="num muted" style={{ fontSize: 11 }}>{st.filename ?? ""}</span>
                      <span className="num muted" style={{ fontSize: 11 }}>{Math.round((st.progress || 0) * 100)}%</span>
                    </div>
                  </>
                )}
                <div className="row between" style={{ marginTop: 12, paddingTop: 11, borderTop: "1px solid var(--line)" }}>
                  <span className="muted" style={{ fontSize: 11 }}>{st?.online === false ? (st.error ? "injoignable" : "hors ligne") : st?.online ? "connecté" : "…"}</span>
                  <button className="btn sm danger icon" title="Retirer" onClick={() => remove(p.id)}><Icon name="trash" size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title="Ajouter une imprimante réseau"
          onClose={() => setOpen(false)}
          footer={<><button className="btn ghost" onClick={() => setOpen(false)}>Annuler</button><button className="btn primary" disabled={!form.name || !form.base_url} onClick={register}>Enregistrer</button></>}
        >
          <div className="field"><label>Nom</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Prusa MK4 #1" autoFocus /></div>
          <div className="field"><label>Type</label>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </div>
          <div className="field"><label>URL</label><input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="http://192.168.1.50:7125" /></div>
          <div className="field"><label>Clé API {form.kind === "moonraker" ? "(optionnel)" : ""} 🔒</label><input value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="X-Api-Key" /></div>
        </Modal>
      )}
    </div>
  );
}
